// <xte-icon> — SVGスプライトの <use> をライトDOMに直接描画するWeb Component。
//
// Shadow DOMを使わない理由（11_SVGアイコンシステム_基盤.md 4.1）:
// スプライトは <body> 直後にライトDOMへ一度だけインライン展開される
// （PHP側 class-xte-icons.php の wp_body_open フック）。<xte-icon> がShadow DOM内に
// <use> を持つと、シャドウ境界を越えてライトDOM側のIDを参照する構成になり、
// Safari（iOS 16以上を対象。_共通規約.md 2.3）で <use> のシャドウ越え参照が
// 不安定になった実績があるため、この構成自体を避ける。
// あわせて、PHPが直接出力する xte_icon()（SSR）と同一の
// `svg.xte-icon` マークアップになるため、どちらの出力経路でも同じCSSセレクタで
// スタイルできる（4.6節）。

const SVG_NS = 'http://www.w3.org/2000/svg';

const SIZE_TOKENS = {
	sm: 16,
	md: 20,
	lg: 24,
	xl: 28,
	'2xl': 32,
};

const DEFAULT_SIZE_TOKEN = 'lg';

// 未定義アイコン名のフォールバック先。無音で消えるより「異常が見える」方が
// 開発中・本番運用中ともに事故に気づきやすい（11 4.5節）。
const FALLBACK_ICON_NAME = 'warning';

export class XteIcon extends HTMLElement {
	static get observedAttributes() {
		return ['name', 'size', 'variant', 'label', 'spin'];
	}

	constructor() {
		super();
		this._svg = document.createElementNS(SVG_NS, 'svg');
		this._use = document.createElementNS(SVG_NS, 'use');
		this._svg.appendChild(this._use);
		this._svg.classList.add('xte-icon');
		// アイコン自体はキーボードフォーカス対象にしない（フォーカスは親ボタン等が担う）。
		this._svg.setAttribute('focusable', 'false');
	}

	connectedCallback() {
		if (this._svg.parentNode !== this) {
			this.appendChild(this._svg);
		}
		this._render();
	}

	// このコンポーネントはイベントリスナー・タイマー・fetch等の外部リソースを
	// 一切保持しないため、disconnectedCallback での AbortController クリーンアップは
	// 不要（IMPLEMENTATION.md 5.2 はリスナー登録がある場合の規約であり、
	// 登録するものがないコンポーネントに空のクリーンアップを追加すると
	// かえって読み手を惑わせるため書かない）。

	attributeChangedCallback() {
		if (this.isConnected) this._render();
	}

	// --- プロパティAPI（JSから element.name = 'heart' のように直接操作できるようにする） ---
	get name() {
		return this.getAttribute('name') || '';
	}
	set name(value) {
		this.setAttribute('name', value);
	}

	get size() {
		return this.getAttribute('size') || DEFAULT_SIZE_TOKEN;
	}
	set size(value) {
		this.setAttribute('size', value);
	}

	get variant() {
		return this.getAttribute('variant') || 'outline';
	}
	set variant(value) {
		this.setAttribute('variant', value);
	}

	get label() {
		return this.getAttribute('label');
	}
	set label(value) {
		if (value == null) this.removeAttribute('label');
		else this.setAttribute('label', value);
	}

	get spin() {
		return this.hasAttribute('spin');
	}
	set spin(value) {
		this.toggleAttribute('spin', Boolean(value));
	}

	// --- 内部処理 ---

	_resolvePixelSize() {
		const raw = this.size;
		if (raw in SIZE_TOKENS) return SIZE_TOKENS[raw];
		const n = Number(raw);
		return Number.isFinite(n) && n > 0 ? n : SIZE_TOKENS[DEFAULT_SIZE_TOKEN];
	}

	/** DOM中のスプライトに指定IDのsymbolが実在するかを確認する。 */
	_symbolExists(id) {
		return Boolean(document.getElementById(id));
	}

	/** name/variant から実際に使用するsymbol IDを決定する（未定義名フォールバックの本体）。 */
	_resolveSymbolId() {
		const name = this.name;

		if (name) {
			const wantsFill = this.variant === 'fill';
			const outlineId = `xte-icon-${name}`;
			const fillId = `xte-icon-${name}-fill`;

			// variant="fill" だがfill版が未実装（57種中41種はfill版を持たない）の場合は
			// 警告を出さず静かにoutlineへフォールバックする。これは正常な運用（1.3節）。
			if (wantsFill && this._symbolExists(fillId)) return fillId;
			if (this._symbolExists(outlineId)) return outlineId;
		}

		// ここに到達するのは (a) name未指定 (b) nameがスプライトに存在しない のいずれか。
		if (name) {
			console.warn(
				`[xte-icon] name="${name}" (variant="${this.variant}") はスプライトに存在しません。` +
					`フォールバックとして "${FALLBACK_ICON_NAME}" を表示します。`
			);
		}
		return `xte-icon-${FALLBACK_ICON_NAME}`;
	}

	_render() {
		const px = this._resolvePixelSize();
		const symbolId = this._resolveSymbolId();

		this._svg.setAttribute('width', String(px));
		this._svg.setAttribute('height', String(px));
		this._use.setAttribute('href', `#${symbolId}`);

		// アクセシビリティの出し分け（label有無で意味アイコン/装飾アイコンを切り替える。
		// _共通規約.md 14章）。
		const label = this.label;
		if (label) {
			this._svg.setAttribute('role', 'img');
			this._svg.setAttribute('aria-label', label);
			this._svg.removeAttribute('aria-hidden');
		} else {
			this._svg.removeAttribute('role');
			this._svg.removeAttribute('aria-label');
			this._svg.setAttribute('aria-hidden', 'true');
		}

		this._svg.classList.toggle('xte-icon--spin', this.spin);
	}
}

if (!customElements.get('xte-icon')) {
	customElements.define('xte-icon', XteIcon);
}
